import React, { useEffect, useMemo, useState } from 'react';
import { ApiEndPoint } from '../services/apiUttiLities';

const PROTECTED_MEDIA_PATHS = ['/api/files/', '/uploads/'];

const browserOrigin = () => {
  if (typeof window === 'undefined' || !window.location?.origin) return 'http://localhost';
  return window.location.origin;
};

const backendBaseUrl = () => {
  const configured = String(ApiEndPoint || '').trim();
  return configured || browserOrigin();
};

const protectedPath = (pathname = '') => (
  PROTECTED_MEDIA_PATHS.some((prefix) => String(pathname).startsWith(prefix))
);

/** Resolve backend-relative media without changing data/blob/external URLs. */
export const resolveMediaUrl = (source) => {
  const raw = String(source || '').trim();
  if (!raw) return '';
  if (!raw.startsWith('/')) return raw;
  try {
    return new URL(raw, `${backendBaseUrl().replace(/\/$/, '')}/`).toString();
  } catch (_) {
    return raw;
  }
};

/**
 * Only the authenticated backend's generic file routes receive the bearer token.
 * This origin check prevents leaking credentials to a third-party URL that happens
 * to use the same path.
 */
export const isProtectedMediaUrl = (source) => {
  const raw = String(source || '').trim();
  if (!raw) return false;
  if (raw.startsWith('/')) return protectedPath(raw);
  try {
    const candidate = new URL(raw, browserOrigin());
    const backend = new URL(backendBaseUrl(), browserOrigin());
    return candidate.origin === backend.origin && protectedPath(candidate.pathname);
  } catch (_) {
    return false;
  }
};

export const fetchAuthenticatedMediaBlob = async (source, { signal } = {}) => {
  const url = resolveMediaUrl(source);
  if (!isProtectedMediaUrl(source)) throw new Error('Refusing to send credentials to an untrusted media URL');
  const token = typeof localStorage !== 'undefined'
    ? localStorage.getItem('access_token')
    : null;
  if (!url || !token) throw new Error('Authenticated media is unavailable');

  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    redirect: 'error',
    signal,
  });
  if (!response.ok) throw new Error(`Media request failed (${response.status})`);

  // Authentication middleware intentionally returns JSON error envelopes with
  // HTTP 200. Detect those without treating a legitimate JSON attachment as an
  // error merely because of its MIME type.
  const contentType = String(response.headers.get('content-type') || '').toLowerCase();
  if (contentType.includes('application/json')) {
    let envelope = null;
    try {
      envelope = await response.clone().json();
    } catch (_) {
      // A JSON attachment can be malformed; it is still a legitimate file.
    }
    if (envelope && envelope.status === false) {
      throw new Error(envelope.message || 'Media access denied');
    }
  }

  const blob = await response.blob();
  if (!blob.size) throw new Error('The media response was empty');
  return blob;
};

export const useAuthenticatedMediaUrl = (source) => {
  const resolvedSource = useMemo(() => resolveMediaUrl(source), [source]);
  const requiresAuthentication = useMemo(() => isProtectedMediaUrl(source), [source]);
  const token = typeof localStorage !== 'undefined'
    ? localStorage.getItem('access_token')
    : null;
  const [result, setResult] = useState({ source: '', url: '', loading: false, error: null });

  useEffect(() => {
    if (!resolvedSource) {
      setResult({ source: '', url: '', loading: false, error: null });
      return undefined;
    }
    if (!requiresAuthentication) {
      setResult({ source: resolvedSource, url: resolvedSource, loading: false, error: null });
      return undefined;
    }

    const controller = new AbortController();
    let objectUrl = '';
    setResult({ source: resolvedSource, url: '', loading: true, error: null });
    fetchAuthenticatedMediaBlob(resolvedSource, { signal: controller.signal })
      .then((blob) => {
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setResult({ source: resolvedSource, url: objectUrl, loading: false, error: null });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setResult({ source: resolvedSource, url: '', loading: false, error });
      });

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [resolvedSource, requiresAuthentication, token]);

  if (result.source !== resolvedSource) {
    return { url: requiresAuthentication ? '' : resolvedSource, loading: requiresAuthentication, error: null };
  }
  return { url: result.url, loading: result.loading, error: result.error };
};

/** Render img/video/audio/iframe with an authenticated blob URL when required. */
const AuthenticatedMedia = ({ as: Element = 'img', src, onAuthenticatedError, ...props }) => {
  const { url, loading, error } = useAuthenticatedMediaUrl(src);

  useEffect(() => {
    if (error && onAuthenticatedError) onAuthenticatedError(error);
  }, [error, onAuthenticatedError]);

  return (
    <Element
      {...props}
      src={url || undefined}
      aria-busy={loading || undefined}
      data-media-error={error ? 'true' : undefined}
    />
  );
};

/** Link/open/download protected media through the same short-lived object URL. */
export const AuthenticatedMediaLink = ({ href, children, onClick, ...props }) => {
  const { url, loading, error } = useAuthenticatedMediaUrl(href);
  return (
    <a
      {...props}
      href={url || undefined}
      aria-disabled={loading || !!error || undefined}
      onClick={(event) => {
        if (!url) event.preventDefault();
        if (onClick) onClick(event);
      }}
    >
      {children}
    </a>
  );
};

export const downloadAuthenticatedMedia = async (source, filename = '') => {
  let objectUrl = '';
  try {
    let href = resolveMediaUrl(source);
    if (!href) throw new Error('Media URL is missing');
    if (isProtectedMediaUrl(source)) {
      const blob = await fetchAuthenticatedMediaBlob(source);
      objectUrl = URL.createObjectURL(blob);
      href = objectUrl;
    }
    const link = document.createElement('a');
    link.href = href;
    link.download = filename || '';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
};

export default AuthenticatedMedia;
